import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  Search,
  Sparkles,
  Brain,
  FileQuestion,
  Users,
  TrendingUp,
  BarChart3,
  Clock,
  RefreshCw,
  Play,
  Filter,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  Target,
  Lightbulb,
  Package,
  Zap,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  ArrowRight,
  Wand2,
  Loader2
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { useSite } from '@/contexts/SiteContext';

// Types
interface Question {
  id: string;
  question_text: string;
  source_type: string;
  answer_authority: string | null;
  opportunity_score: number;
  search_volume: number;
  cpc: number;
  status: string;
  created_at: string;
}

// Product-centric types (Story 77)
interface ProductSearchResult {
  id: string;
  name: string;
  brand: string;
  category: string;
  price: number;
  url: string;
  image_url?: string;
  sku?: string;
  question_count: number;
  faq_count: number;
  has_legacy_faqs?: boolean;
  legacy_faq_count?: number;
}

// Legacy FAQ from aeo_page_generations
interface LegacyFAQ {
  id: string;
  question_text: string;
  answer_text: string;
  source: string;
  status: string;
  created_at: string;
}

interface ProductQuestion {
  id: string;
  question_text: string;
  source_type: string;
  search_volume: number;
  cpc: number;
  opportunity_score: number;
  intent_cluster?: string;
  answer_authority?: string;
  answerability?: {
    authority: string;
    confidence: number;
    available_sources: string[];
    missing_data: string[];
    strategy?: string;
  };
  has_faq?: boolean;
  faq_status?: string;
}

interface ProductFAQ {
  id: string;
  question_text: string;
  answer_text: string;
  grounding_score: number;
  status: string;
  source_type?: string;
  warnings?: string[];
  created_at: string;
}

interface ProductQuestionSummary {
  total: number;
  by_source: Record<string, number>;
  answerable: number;
  needs_research: number;
  total_search_volume: number;
}

interface FAQ {
  id: string;
  question_text: string;
  answer_text: string;
  grounding_score: number;
  completeness_score: number;
  relevance_score: number;
  has_existing_aeo_match: boolean;
  status: string;
  opportunity_score: number;
  search_volume: number;
  source_type: string;
  created_at: string;
}

interface RoadmapItem {
  id: string;
  question_text: string;
  authority_required: string;
  research_methodology: string;
  priority: string;
  status: string;
  demand_score: number;
  search_volume: number;
  assigned_expert_name: string | null;
  created_at: string;
}

interface Stats {
  total_questions: number;
  by_source: Record<string, number>;
  by_status: Record<string, number>;
  by_authority: Record<string, number>;
  avg_opportunity_score: number;
}

interface DashboardMetrics {
  stats: {
    total_faqs: string;
    draft: string;
    pending_review: string;
    approved: string;
    rejected: string;
    avg_grounding_score: string;
    with_existing_match: string;
    new_questions: string;
  };
  sources: Array<{ source_type: string; count: string; avg_grounding: string }>;
  top_gaps: Array<{
    question_text: string;
    opportunity_score: string;
    search_volume: number;
    source_type: string;
  }>;
  quality_distribution: Array<{ quality_tier: string; count: string }>;
}

export default function DemandIntelligenceDashboard() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('discovery');
  const [generatingGapContent, setGeneratingGapContent] = useState<string | null>(null);

  // Discovery state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState(false);

  // FAQ state
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [products, setProducts] = useState<Array<{ id: string; name: string }>>([]);

  // Roadmap state
  const [roadmapItems, setRoadmapItems] = useState<RoadmapItem[]>([]);
  const [roadmapStats, setRoadmapStats] = useState<any>(null);
  const [roadmapLoading, setRoadmapLoading] = useState(false);

  // Validation state
  const [dashboard, setDashboard] = useState<DashboardMetrics | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');

  // Product-centric state (Story 77)
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductSearchResult[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProductForFAQ, setSelectedProductForFAQ] = useState<ProductSearchResult | null>(null);
  const [productQuestions, setProductQuestions] = useState<ProductQuestion[]>([]);
  const [productQuestionSummary, setProductQuestionSummary] = useState<ProductQuestionSummary | null>(null);
  const [productQuestionsLoading, setProductQuestionsLoading] = useState(false);
  const [selectedProductQuestions, setSelectedProductQuestions] = useState<Set<string>>(new Set());
  const [productFAQs, setProductFAQs] = useState<ProductFAQ[]>([]);
  const [productFAQsLoading, setProductFAQsLoading] = useState(false);
  const [generatingProductAnswers, setGeneratingProductAnswers] = useState(false);
  const [productSourceFilter, setProductSourceFilter] = useState<string>('all');
  const [productAuthorityFilter, setProductAuthorityFilter] = useState<string>('all');
  const [productSortBy, setProductSortBy] = useState<string>('opportunity_score');
  const [expandedProductFaq, setExpandedProductFaq] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingFaqId, setRejectingFaqId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Legacy FAQs state (side-by-side comparison)
  const [legacyFAQs, setLegacyFAQs] = useState<LegacyFAQ[]>([]);
  const [legacyFAQsLoading, setLegacyFAQsLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [generatingNewFAQs, setGeneratingNewFAQs] = useState(false);

  // Ask Discovery state - now keyed by questionId for inline display
  const [askingQuestionId, setAskingQuestionId] = useState<string | null>(null);
  const [askResults, setAskResults] = useState<Record<string, {
    question: string;
    discoveryAnswer: string;
    aeoAnswer?: string;
    intent?: string;
    sources?: string[];
    featuredSnippetReady?: boolean;
  }>>({});
  const [askLoading, setAskLoading] = useState(false);
  const [convertingToAEO, setConvertingToAEO] = useState(false);
  const [savingFAQ, setSavingFAQ] = useState<string | null>(null);

  const siteId = selectedSite?.id;

  // Fetch questions on mount
  useEffect(() => {
    if (siteId) {
      fetchQuestions();
      fetchStats();
      fetchProducts();
    }
  }, [siteId]);

  // Fetch data when tab changes
  useEffect(() => {
    if (!siteId) return;

    if (activeTab === 'roadmap') {
      fetchRoadmap();
    } else if (activeTab === 'validation') {
      fetchDashboard();
    }
  }, [activeTab, siteId]);

  const fetchQuestions = async () => {
    if (!siteId) return;

    try {
      const params = new URLSearchParams({ siteId, limit: '100' });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSource !== 'all') params.append('sourceType', filterSource);

      const response = await authFetch(`/api/demand/questions?${params}`);
      if (!response.ok) throw new Error('Failed to fetch questions');

      const data = await response.json();
      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load questions');
    }
  };

  const fetchStats = async () => {
    if (!siteId) return;

    try {
      const response = await authFetch(`/api/demand/stats/${siteId}`);
      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchProducts = async () => {
    if (!siteId) return;

    try {
      const response = await authFetch(`/api/demand/products?siteId=${siteId}&limit=100`);
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchRoadmap = async () => {
    if (!siteId) return;

    setRoadmapLoading(true);
    try {
      const response = await authFetch(`/api/demand/roadmap/${siteId}`);
      if (!response.ok) throw new Error('Failed to fetch roadmap');

      const data = await response.json();
      setRoadmapItems(data.roadmap?.items || []);
      setRoadmapStats(data.roadmap?.stats || null);
    } catch (err) {
      console.error('Error fetching roadmap:', err);
      setError(err instanceof Error ? err.message : 'Failed to load roadmap');
    } finally {
      setRoadmapLoading(false);
    }
  };

  const fetchDashboard = async () => {
    if (!siteId) return;

    setValidationLoading(true);
    try {
      const response = await authFetch(`/api/demand/validation/dashboard?siteId=${siteId}`);
      if (!response.ok) throw new Error('Failed to fetch dashboard');

      const data = await response.json();
      setDashboard(data.dashboard);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setValidationLoading(false);
    }
  };

  const handleDiscover = async () => {
    if (!siteId) return;

    setDiscoveryLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch('/api/demand/discover', {
        method: 'POST',
        body: JSON.stringify({
          siteId,
          sources: ['google_ads', 'paa', 'review', 'wheel']
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Discovery failed');
      }

      const data = await response.json();
      setSuccess(data.message);
      fetchQuestions();
      fetchStats();
    } catch (err) {
      console.error('Discovery error:', err);
      setError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleClassify = async () => {
    if (!siteId) return;

    setClassifyLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(`/api/demand/classify/${siteId}`, {
        method: 'POST',
        body: JSON.stringify({ batchSize: 50 })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Classification failed');
      }

      const data = await response.json();
      setSuccess(data.message);
      fetchQuestions();
      fetchStats();
    } catch (err) {
      console.error('Classification error:', err);
      setError(err instanceof Error ? err.message : 'Classification failed');
    } finally {
      setClassifyLoading(false);
    }
  };

  const handleGenerateFAQs = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    setFaqLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(`/api/demand/generate/${selectedProduct}`, {
        method: 'POST',
        body: JSON.stringify({ maxQuestions: 20 })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Generation failed');
      }

      const data = await response.json();
      setSuccess(data.message);
      fetchFAQs();
    } catch (err) {
      console.error('Generation error:', err);
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setFaqLoading(false);
    }
  };

  const fetchFAQs = async () => {
    if (!selectedProduct) return;

    try {
      const response = await authFetch(`/api/demand/faqs/${selectedProduct}`);
      if (!response.ok) throw new Error('Failed to fetch FAQs');

      const data = await response.json();
      setFaqs(data.faqs || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
    }
  };

  const handleCreateRoadmap = async () => {
    if (!siteId) return;

    setRoadmapLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(`/api/demand/roadmap/${siteId}/create`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Roadmap creation failed');
      }

      const data = await response.json();
      setSuccess(data.message);
      fetchRoadmap();
    } catch (err) {
      console.error('Roadmap error:', err);
      setError(err instanceof Error ? err.message : 'Roadmap creation failed');
    } finally {
      setRoadmapLoading(false);
    }
  };

  // Generate content for a demand gap question
  const handleCreateGapContent = async (gapQuestion: string) => {
    if (!siteId) return;
    setGeneratingGapContent(gapQuestion);

    try {
      // Infer best page type from question pattern
      const q = gapQuestion.toLowerCase();
      let endpoint = '/api/business-about/category/buying-guide';
      let body: any = { siteId, language: 'es' };
      let pageType = 'buying_guide';

      if (q.includes(' vs ') || q.includes(' versus ') || q.includes('compare') || q.includes('difference between')) {
        // Extract entities for comparison
        const vsMatch = gapQuestion.match(/(.+?)\s+(?:vs|versus|vs\.)\s+(.+?)(?:\?|$)/i);
        if (vsMatch) {
          endpoint = '/api/business-about/category/comparison-page';
          body.entityA = vsMatch[1].trim();
          body.entityB = vsMatch[2].trim();
          body.comparisonType = 'brand';
          pageType = 'comparison';
        }
      } else if (q.includes('best ') && q.includes(' for ')) {
        // "Best X for Y" → use case page
        const forMatch = gapQuestion.match(/best .+? for (.+?)(?:\?|$)/i);
        endpoint = '/api/business-about/category/use-case';
        body.useCase = forMatch?.[1]?.trim() || gapQuestion;
        pageType = 'use_case';
      } else if (q.includes('beginner') || q.includes('professional') || q.includes('expert') || q.includes('kids')) {
        endpoint = '/api/business-about/category/persona';
        body.persona = gapQuestion;
        pageType = 'persona';
      } else if (q.includes('best ') || q.includes('top ')) {
        endpoint = '/api/business-about/category/best-of';
        pageType = 'best_of';
      }

      // CategoryPath would ideally come from cluster→category mapping
      // For now, content is generated without a specific category (the endpoints handle this)
      if (!body.categoryPath) body.categoryPath = 'General';

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        toast({
          title: `${pageType.replace('_', ' ')} page created`,
          description: `Content generated for "${gapQuestion.substring(0, 50)}..."`,
        });
      } else {
        toast({
          title: 'Generation failed',
          description: data.error || 'Could not generate content',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Content generation failed',
        variant: 'destructive',
      });
    } finally {
      setGeneratingGapContent(null);
    }
  };

  const handleApproveFAQ = async (faqId: string) => {
    try {
      const response = await authFetch(`/api/demand/validation/approve/${faqId}`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Approval failed');
      }

      setSuccess('FAQ approved');
      fetchFAQs();
      fetchDashboard();
    } catch (err) {
      console.error('Approval error:', err);
      setError(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  const handleRejectFAQ = async (faqId: string, reason: string) => {
    try {
      const response = await authFetch(`/api/demand/validation/reject/${faqId}`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Rejection failed');
      }

      setSuccess('FAQ rejected');
      fetchFAQs();
      fetchDashboard();
    } catch (err) {
      console.error('Rejection error:', err);
      setError(err instanceof Error ? err.message : 'Rejection failed');
    }
  };

  const getSourceBadgeColor = (source: string) => {
    const colors: Record<string, string> = {
      google_ads: 'bg-blue-100 text-blue-800',
      paa: 'bg-green-100 text-green-800',
      review: 'bg-purple-100 text-purple-800',
      wheel: 'bg-orange-100 text-orange-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  const getAuthorityBadgeColor = (authority: string | null) => {
    if (!authority) return 'bg-gray-100 text-gray-800';
    const colors: Record<string, string> = {
      product_data: 'bg-emerald-100 text-emerald-800',
      domain_knowledge: 'bg-cyan-100 text-cyan-800',
      expert_required: 'bg-amber-100 text-amber-800',
      research_needed: 'bg-red-100 text-red-800'
    };
    return colors[authority] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadgeColor = (priority: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      discovered: 'bg-blue-100 text-blue-800',
      classified: 'bg-purple-100 text-purple-800',
      answered: 'bg-green-100 text-green-800',
      roadmap: 'bg-amber-100 text-amber-800',
      draft: 'bg-gray-100 text-gray-800',
      approved: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Product-centric functions (Story 77)
  const searchProductsForFAQ = useCallback(async () => {
    if (!siteId || !productSearchQuery || productSearchQuery.length < 2) return;

    setProductSearching(true);
    try {
      // Build query params - include category filter if set
      let url = `/api/demand/products/search?siteId=${siteId}&q=${encodeURIComponent(productSearchQuery)}&limit=20`;
      if (categoryFilter) {
        url += `&category=${encodeURIComponent(categoryFilter)}`;
      }
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setProductSearchResults(data.products || []);
      }
    } catch (err) {
      console.error('Product search error:', err);
    } finally {
      setProductSearching(false);
    }
  }, [siteId, productSearchQuery, categoryFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchQuery.length >= 2) {
        searchProductsForFAQ();
      } else {
        setProductSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery, searchProductsForFAQ]);

  useEffect(() => {
    if (selectedProductForFAQ) {
      fetchProductQuestions(selectedProductForFAQ.id);
      fetchProductFAQs(selectedProductForFAQ.id);
      fetchLegacyFAQs(selectedProductForFAQ.id);
      // Sync with Generation tab's product selector
      setSelectedProduct(selectedProductForFAQ.id);
      // Add to products array if not already present
      setProducts(prev => {
        if (!prev.find(p => p.id === selectedProductForFAQ.id)) {
          return [...prev, { id: selectedProductForFAQ.id, name: selectedProductForFAQ.name }];
        }
        return prev;
      });
    }
  }, [selectedProductForFAQ]);

  // Fetch legacy FAQs from aeo_page_generations
  const fetchLegacyFAQs = async (productId: string) => {
    setLegacyFAQsLoading(true);
    try {
      const response = await authFetch(`/api/demand/product/${productId}/legacy-faqs`);
      if (response.ok) {
        const data = await response.json();
        setLegacyFAQs(data.legacyFaqs || []);
      }
    } catch (err) {
      console.error('Error fetching legacy FAQs:', err);
      setLegacyFAQs([]);
    } finally {
      setLegacyFAQsLoading(false);
    }
  };

  const fetchProductQuestions = async (productId: string) => {
    setProductQuestionsLoading(true);
    try {
      const response = await authFetch(
        `/api/demand/product/${productId}/questions?includeGenerated=true`
      );
      if (response.ok) {
        const data = await response.json();
        setProductQuestions(data.questions || []);
        setProductQuestionSummary(data.summary || null);
        setSelectedProductQuestions(new Set());
      }
    } catch (err) {
      console.error('Error fetching product questions:', err);
    } finally {
      setProductQuestionsLoading(false);
    }
  };

  const fetchProductFAQs = async (productId: string) => {
    setProductFAQsLoading(true);
    try {
      const response = await authFetch(`/api/demand/product/${productId}/faqs`);
      if (response.ok) {
        const data = await response.json();
        setProductFAQs(data.faqs || []);
      }
    } catch (err) {
      console.error('Error fetching product FAQs:', err);
    } finally {
      setProductFAQsLoading(false);
    }
  };

  const handleGenerateProductAnswers = async () => {
    if (!selectedProductForFAQ || selectedProductQuestions.size === 0) return;

    setGeneratingProductAnswers(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(
        `/api/demand/product/${selectedProductForFAQ.id}/generate-answers`,
        {
          method: 'POST',
          body: JSON.stringify({
            questionIds: Array.from(selectedProductQuestions),
            optimizeForLLM: true,
            validateGrounding: true
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || `Generated ${data.faqs?.length || 0} FAQs`);
        setSelectedProductQuestions(new Set());
        fetchProductFAQs(selectedProductForFAQ.id);
        fetchProductQuestions(selectedProductForFAQ.id);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Generation failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGeneratingProductAnswers(false);
    }
  };

  const handleApproveProductFAQ = async (faqId: string) => {
    try {
      const response = await authFetch(`/api/demand/validation/approve/${faqId}`, {
        method: 'POST'
      });
      if (response.ok) {
        setSuccess('FAQ approved');
        if (selectedProductForFAQ) fetchProductFAQs(selectedProductForFAQ.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  const handleRejectProductFAQ = async () => {
    if (!rejectingFaqId || !rejectReason) return;

    try {
      const response = await authFetch(`/api/demand/validation/reject/${rejectingFaqId}`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason })
      });
      if (response.ok) {
        setSuccess('FAQ rejected');
        setRejectDialogOpen(false);
        setRejectingFaqId(null);
        setRejectReason('');
        if (selectedProductForFAQ) fetchProductFAQs(selectedProductForFAQ.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    }
  };

  const toggleProductQuestionSelection = (questionId: string) => {
    const newSelected = new Set(selectedProductQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedProductQuestions(newSelected);
  };

  // Ask Discovery for an answer to a question
  const handleAskQuestion = async (question: ProductQuestion, convertToAEO: boolean = false) => {
    if (!selectedProductForFAQ) return;

    setAskingQuestionId(question.id);
    setAskLoading(true);
    if (convertToAEO) setConvertingToAEO(true);

    try {
      const response = await authFetch(
        `/api/demand/product/${selectedProductForFAQ.id}/ask-question`,
        {
          method: 'POST',
          body: JSON.stringify({
            questionText: question.question_text,
            convertToAEO
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[DemandIntelligence] Ask response:', data);
        setAskResults(prev => ({
          ...prev,
          [question.id]: {
            question: question.question_text,
            discoveryAnswer: data.discoveryAnswer,
            aeoAnswer: data.aeoAnswer,
            intent: data.intent,
            sources: data.sources?.map((s: { type?: string; title?: string; url?: string; count?: number }) =>
              s.title || s.url || (s.type ? `${s.type} (${s.count || 0})` : 'Unknown')
            ) || [],
            featuredSnippetReady: data.featuredSnippetReady
          }
        }));
      } else {
        const data = await response.json();
        console.error('[DemandIntelligence] Ask error:', data);
        setError(data.error || 'Failed to get answer');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to ask question');
    } finally {
      setAskLoading(false);
      setAskingQuestionId(null);
      setConvertingToAEO(false);
    }
  };

  // Convert existing discovery answer to AEO format
  const handleConvertToAEO = async (questionId: string) => {
    if (!selectedProductForFAQ) return;
    const result = askResults[questionId];
    if (!result) return;

    setConvertingToAEO(true);
    setAskingQuestionId(questionId);

    try {
      const response = await authFetch(
        `/api/demand/product/${selectedProductForFAQ.id}/ask-question`,
        {
          method: 'POST',
          body: JSON.stringify({
            questionText: result.question,
            convertToAEO: true
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAskResults(prev => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            aeoAnswer: data.aeoAnswer,
            featuredSnippetReady: data.featuredSnippetReady
          }
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert to AEO');
    } finally {
      setConvertingToAEO(false);
      setAskingQuestionId(null);
    }
  };

  // Save FAQ to database
  const handleSaveFAQ = async (questionId: string) => {
    if (!selectedProductForFAQ) return;
    const result = askResults[questionId];
    if (!result || !result.aeoAnswer) return;

    setSavingFAQ(questionId);

    try {
      const response = await authFetch(
        `/api/demand/product/${selectedProductForFAQ.id}/save-faq`,
        {
          method: 'POST',
          body: JSON.stringify({
            questionId,
            questionText: result.question,
            answerText: result.aeoAnswer,
            discoveryAnswer: result.discoveryAnswer,
            sources: result.sources
          })
        }
      );

      if (response.ok) {
        // Remove from askResults and show success
        setAskResults(prev => {
          const newResults = { ...prev };
          delete newResults[questionId];
          return newResults;
        });
        // Refresh FAQs
        fetchProductFAQs(selectedProductForFAQ.id);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save FAQ');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save FAQ');
    } finally {
      setSavingFAQ(null);
    }
  };

  // Close/dismiss an answer
  const handleDismissAnswer = (questionId: string) => {
    setAskResults(prev => {
      const newResults = { ...prev };
      delete newResults[questionId];
      return newResults;
    });
  };

  const selectAllAnswerableProductQuestions = () => {
    const answerable = productQuestions.filter(
      q => q.answerability?.authority === 'can_answer'
    );
    setSelectedProductQuestions(new Set(answerable.map(q => q.id)));
  };

  const filteredProductQuestions = productQuestions.filter(q => {
    if (productSourceFilter !== 'all' && q.source_type !== productSourceFilter) return false;
    if (productAuthorityFilter !== 'all' && q.answerability?.authority !== productAuthorityFilter) return false;
    return true;
  }).sort((a, b) => {
    if (productSortBy === 'opportunity_score') return (b.opportunity_score || 0) - (a.opportunity_score || 0);
    if (productSortBy === 'search_volume') return (b.search_volume || 0) - (a.search_volume || 0);
    if (productSortBy === 'cpc') return (b.cpc || 0) - (a.cpc || 0);
    return 0;
  });

  const getProductSourceBadgeColor = (source: string) => {
    const colors: Record<string, string> = {
      google_ads: 'bg-blue-100 text-blue-800',
      paa: 'bg-green-100 text-green-800',
      review: 'bg-purple-100 text-purple-800',
      wheel: 'bg-orange-100 text-orange-800',
      category: 'bg-cyan-100 text-cyan-800',
      ai_research: 'bg-pink-100 text-pink-800',
      competitor: 'bg-yellow-100 text-yellow-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  const getProductAuthorityBadgeColor = (authority: string | undefined) => {
    if (!authority) return 'bg-gray-100 text-gray-800';
    const colors: Record<string, string> = {
      can_answer: 'bg-emerald-100 text-emerald-800',
      needs_research: 'bg-amber-100 text-amber-800',
      needs_expert: 'bg-red-100 text-red-800',
      unknown: 'bg-gray-100 text-gray-800'
    };
    return colors[authority] || 'bg-gray-100 text-gray-800';
  };

  const getGroundingColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view demand intelligence data.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Demand Intelligence</h1>
          <p className="text-muted-foreground">
            Discover high-demand questions and generate targeted FAQ content
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="product-faqs" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Product FAQs
          </TabsTrigger>
          <TabsTrigger value="discovery" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Discovery
          </TabsTrigger>
          <TabsTrigger value="generation" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            FAQ Generation
          </TabsTrigger>
          <TabsTrigger value="roadmap" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Knowledge Roadmap
          </TabsTrigger>
          <TabsTrigger value="validation" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Validation
          </TabsTrigger>
        </TabsList>

        {/* Product FAQs Tab (Story 77) */}
        <TabsContent value="product-faqs" className="space-y-4">
          {/* Product Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Products
              </CardTitle>
              <CardDescription>
                Search by product name, category, SKU, or brand to compare FAQs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by product name, SKU, brand..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Input
                  placeholder="Filter by category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-48"
                />
                {productSearching && (
                  <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground self-center" />
                )}
              </div>

              {/* Search Results */}
              {productSearchResults.length > 0 && !selectedProductForFAQ && (
                <div className="mt-4 border rounded-lg divide-y max-h-80 overflow-y-auto">
                  {productSearchResults.map((product) => (
                    <button
                      key={product.id}
                      className="w-full p-3 hover:bg-muted/50 text-left flex items-center gap-4"
                      onClick={() => {
                        setSelectedProductForFAQ(product);
                        setProductSearchResults([]);
                        setProductSearchQuery('');
                        setLegacyFAQs([]);
                      }}
                    >
                      <Package className="h-8 w-8 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{product.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {product.brand} - {product.category}
                          {product.sku && <span className="ml-2 text-xs bg-muted px-1 rounded">SKU: {product.sku}</span>}
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <div>{product.question_count} questions</div>
                        <div className="text-muted-foreground">{product.faq_count} new FAQs</div>
                        {product.has_legacy_faqs && (
                          <div className="text-amber-600">{product.legacy_faq_count} legacy FAQs</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selected Product */}
          {selectedProductForFAQ && (
            <>
              {/* Product Header */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <Package className="h-12 w-12 text-muted-foreground" />
                      <div>
                        <h2 className="text-xl font-semibold">{selectedProductForFAQ.name}</h2>
                        <p className="text-muted-foreground">
                          {selectedProductForFAQ.brand} - {selectedProductForFAQ.category}
                        </p>
                        {selectedProductForFAQ.url && (
                          <a
                            href={selectedProductForFAQ.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                          >
                            View Product <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedProductForFAQ(null);
                        setProductQuestions([]);
                        setProductFAQs([]);
                        setLegacyFAQs([]);
                        setProductQuestionSummary(null);
                      }}
                    >
                      Change Product
                    </Button>
                  </div>

                  {/* Summary Stats */}
                  {productQuestionSummary && (
                    <div className="grid grid-cols-4 gap-4 mt-6">
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="text-2xl font-bold">{productQuestionSummary.total}</div>
                        <div className="text-sm text-muted-foreground">Total Questions</div>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-emerald-700">{productQuestionSummary.answerable}</div>
                        <div className="text-sm text-muted-foreground">Answerable</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-amber-700">{productQuestionSummary.needs_research}</div>
                        <div className="text-sm text-muted-foreground">Need Research</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-blue-700">
                          {productQuestionSummary.total_search_volume?.toLocaleString() || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Search Volume</div>
                      </div>
                    </div>
                  )}

                  {/* Source breakdown */}
                  {productQuestionSummary?.by_source && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {Object.entries(productQuestionSummary.by_source).map(([source, count]) => (
                        <Badge key={source} variant="outline" className="gap-1">
                          {source}: {count}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Questions Section */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileQuestion className="h-5 w-5" />
                        Questions from All Sources
                      </CardTitle>
                      <CardDescription>
                        Select questions to generate FAQ answers
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllAnswerableProductQuestions}
                        disabled={productQuestionsLoading}
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Select Answerable
                      </Button>
                      <Button
                        onClick={handleGenerateProductAnswers}
                        disabled={selectedProductQuestions.size === 0 || generatingProductAnswers}
                        className="flex items-center gap-2"
                      >
                        {generatingProductAnswers ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        Generate Answers ({selectedProductQuestions.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex gap-4 mb-4">
                    <Select value={productSourceFilter} onValueChange={setProductSourceFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="google_ads">Google Ads</SelectItem>
                        <SelectItem value="paa">PAA</SelectItem>
                        <SelectItem value="review">Reviews</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="ai_research">AI Research</SelectItem>
                        <SelectItem value="competitor">Competitor</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={productAuthorityFilter} onValueChange={setProductAuthorityFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by answerability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="can_answer">Can Answer</SelectItem>
                        <SelectItem value="needs_research">Needs Research</SelectItem>
                        <SelectItem value="needs_expert">Needs Expert</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={productSortBy} onValueChange={setProductSortBy}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opportunity_score">Opportunity Score</SelectItem>
                        <SelectItem value="search_volume">Search Volume</SelectItem>
                        <SelectItem value="cpc">CPC</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      variant="outline"
                      onClick={() => fetchProductQuestions(selectedProductForFAQ.id)}
                      disabled={productQuestionsLoading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${productQuestionsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>

                  {/* Questions Table */}
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Answerability</TableHead>
                          <TableHead className="text-right">Volume</TableHead>
                          <TableHead className="text-right">Opportunity</TableHead>
                          <TableHead className="w-24 text-center">Ask</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productQuestionsLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8">
                              <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                            </TableCell>
                          </TableRow>
                        ) : filteredProductQuestions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              No questions found. Try refreshing or adjusting filters.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredProductQuestions.map((q) => (
                            <React.Fragment key={q.id}>
                            <TableRow className="hover:bg-muted/50">
                              <TableCell>
                                <Checkbox
                                  checked={selectedProductQuestions.has(q.id)}
                                  onCheckedChange={() => toggleProductQuestionSelection(q.id)}
                                />
                              </TableCell>
                              <TableCell className="max-w-md">
                                <div className="flex items-start gap-2">
                                  <div className="line-clamp-2 flex-1">{q.question_text}</div>
                                  {q.has_faq && (
                                    <Badge className="bg-green-600 text-white text-xs shrink-0">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      Answered
                                    </Badge>
                                  )}
                                </div>
                                {q.answerability?.strategy && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {q.answerability.strategy}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge className={getProductSourceBadgeColor(q.source_type)}>
                                  {q.source_type}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={getProductAuthorityBadgeColor(q.answerability?.authority)}>
                                  {q.answerability?.authority?.replace('_', ' ') || 'Unknown'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {q.search_volume?.toLocaleString() || '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Progress
                                    value={q.opportunity_score || 0}
                                    className="w-16 h-2"
                                  />
                                  <span className="text-sm w-8">
                                    {Math.round(q.opportunity_score || 0)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleAskQuestion(q)}
                                  disabled={askLoading && askingQuestionId === q.id}
                                  title="Ask Discovery engine"
                                >
                                  {askLoading && askingQuestionId === q.id ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <MessageCircle className="h-4 w-4" />
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                            {/* Inline Answer Row */}
                            {askResults[q.id] && (
                              <TableRow className="bg-slate-800/50 hover:bg-slate-800/50">
                                <TableCell colSpan={7} className="py-4">
                                  <div className="pl-8 space-y-3">
                                    {/* Discovery Answer */}
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                        <Brain className="h-3 w-3" />
                                        Discovery Response
                                      </div>
                                      <div className="text-sm text-slate-300">
                                        {askResults[q.id].discoveryAnswer}
                                      </div>
                                    </div>

                                    {/* AEO Answer */}
                                    {askResults[q.id].aeoAnswer && (
                                      <div>
                                        <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                          <Wand2 className="h-3 w-3" />
                                          AEO-Ready Answer
                                          {askResults[q.id].featuredSnippetReady && (
                                            <Badge className="ml-2 bg-green-900 text-green-300 text-xs">
                                              Featured Snippet Ready
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-sm text-green-400">
                                          {askResults[q.id].aeoAnswer}
                                        </div>
                                      </div>
                                    )}

                                    {/* Sources */}
                                    {askResults[q.id].sources && askResults[q.id].sources!.length > 0 && (
                                      <div className="text-xs text-muted-foreground">
                                        Sources: {askResults[q.id].sources!.slice(0, 3).join(', ')}
                                      </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 pt-2">
                                      {!askResults[q.id].aeoAnswer ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleConvertToAEO(q.id)}
                                          disabled={convertingToAEO && askingQuestionId === q.id}
                                        >
                                          {convertingToAEO && askingQuestionId === q.id ? (
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                          ) : (
                                            <Wand2 className="h-3 w-3 mr-1" />
                                          )}
                                          Convert to AEO
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="default"
                                          className="bg-green-700 hover:bg-green-600"
                                          onClick={() => handleSaveFAQ(q.id)}
                                          disabled={savingFAQ === q.id}
                                        >
                                          {savingFAQ === q.id ? (
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                          ) : (
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                          )}
                                          Save FAQ
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDismissAnswer(q.id)}
                                      >
                                        <XCircle className="h-3 w-3 mr-1" />
                                        Dismiss
                                      </Button>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                </CardContent>
              </Card>

              {/* Side-by-Side FAQ Comparison */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        FAQ Comparison
                      </CardTitle>
                      <CardDescription>
                        Compare legacy FAQs (left) with newly generated FAQs (right)
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          fetchLegacyFAQs(selectedProductForFAQ.id);
                          fetchProductFAQs(selectedProductForFAQ.id);
                        }}
                        disabled={legacyFAQsLoading || productFAQsLoading}
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${(legacyFAQsLoading || productFAQsLoading) ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                      <Button
                        onClick={handleGenerateProductAnswers}
                        disabled={selectedProductQuestions.size === 0 || generatingProductAnswers}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                      >
                        {generatingProductAnswers ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        Generate FAQs
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Column: Legacy FAQs */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                          Legacy FAQs ({legacyFAQs.length})
                        </Badge>
                        <span className="text-sm text-muted-foreground">From AEO Generation</span>
                      </div>
                      {legacyFAQsLoading ? (
                        <div className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        </div>
                      ) : legacyFAQs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                          No legacy FAQs found for this product.
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                          {legacyFAQs.map((faq) => (
                            <div
                              key={faq.id}
                              className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                            >
                              <div className="font-medium text-gray-900 dark:text-white text-sm">
                                Q: {faq.question_text}
                              </div>
                              <div className="mt-2 text-gray-700 dark:text-slate-300 text-sm">
                                A: {faq.answer_text}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {faq.source}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right Column: Newly Generated FAQs */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                          New FAQs ({productFAQs.length})
                        </Badge>
                        <span className="text-sm text-muted-foreground">From New Algorithm</span>
                      </div>
                      {productFAQsLoading ? (
                        <div className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        </div>
                      ) : productFAQs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                          <p>No new FAQs generated yet.</p>
                          <p className="text-sm mt-2">Select questions above and click "Generate FAQs"</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                          {productFAQs.map((faq) => (
                            <div
                              key={faq.id}
                              className={`border rounded-lg p-4 ${
                                faq.status === 'approved' ? 'border-green-600 bg-green-50 dark:bg-green-900/30' :
                                faq.status === 'rejected' ? 'border-red-600 bg-red-50 dark:bg-red-900/30' :
                                'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'
                              }`}
                            >
                              <div className="font-medium text-gray-900 dark:text-white text-sm">
                                Q: {faq.question_text}
                              </div>
                              <div className="mt-2 text-gray-700 dark:text-slate-300 text-sm">
                                A: {expandedProductFaq === faq.id ? faq.answer_text : (
                                  <span className="line-clamp-3">{faq.answer_text}</span>
                                )}
                              </div>
                              {faq.answer_text.length > 200 && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="px-0 h-auto text-xs"
                                  onClick={() => setExpandedProductFaq(expandedProductFaq === faq.id ? null : faq.id)}
                                >
                                  {expandedProductFaq === faq.id ? 'Show less' : 'Show more'}
                                </Button>
                              )}
                              <div className="mt-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Badge className={getStatusBadgeColor(faq.status)}>
                                    {faq.status}
                                  </Badge>
                                  <span className={`text-xs ${getGroundingColor(faq.grounding_score)}`}>
                                    {Math.round((faq.grounding_score || 0) * 100)}% grounded
                                  </span>
                                </div>
                                {faq.status === 'draft' && (
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
                                      onClick={() => handleApproveProductFAQ(faq.id)}
                                      title="Approve"
                                    >
                                      <ThumbsUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                      onClick={() => {
                                        setRejectingFaqId(faq.id);
                                        setRejectDialogOpen(true);
                                      }}
                                      title="Reject"
                                    >
                                      <ThumbsDown className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {/* Warnings */}
                              {faq.warnings && faq.warnings.length > 0 && (
                                <div className="mt-2 p-1.5 bg-amber-100 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-700 rounded text-xs text-amber-800 dark:text-amber-300">
                                  {faq.warnings.join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Discovery Tab */}
        <TabsContent value="discovery" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats?.total_questions || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avg Opportunity Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {parseFloat(String(stats?.avg_opportunity_score || 0)).toFixed(1)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Classified
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats?.by_status?.classified || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Answered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats?.by_status?.answered || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Question Discovery</CardTitle>
              <CardDescription>
                Discover questions from Google Ads, PAA, reviews, and question wheels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button
                  onClick={handleDiscover}
                  disabled={discoveryLoading}
                  className="flex items-center gap-2"
                >
                  {discoveryLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Discover Questions
                </Button>
                <Button
                  onClick={handleClassify}
                  disabled={classifyLoading}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  {classifyLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4" />
                  )}
                  Classify Questions
                </Button>
              </div>

              {/* Filters */}
              <div className="flex gap-4">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="discovered">Discovered</SelectItem>
                    <SelectItem value="classified">Classified</SelectItem>
                    <SelectItem value="answered">Answered</SelectItem>
                    <SelectItem value="roadmap">Roadmap</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="paa">PAA</SelectItem>
                    <SelectItem value="review">Reviews</SelectItem>
                    <SelectItem value="wheel">Question Wheel</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchQuestions}>
                  <Filter className="h-4 w-4 mr-2" />
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Questions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Discovered Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Question</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Authority</TableHead>
                    <TableHead>Opportunity</TableHead>
                    <TableHead>Volume</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {questions.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell className="max-w-md">
                        <span className="line-clamp-2">{q.question_text}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSourceBadgeColor(q.source_type)}>
                          {q.source_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {q.answer_authority ? (
                          <Badge className={getAuthorityBadgeColor(q.answer_authority)}>
                            {q.answer_authority.replace('_', ' ')}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={parseFloat(String(q.opportunity_score || 0))} className="w-16" />
                          <span className="text-sm">{parseFloat(String(q.opportunity_score || 0)).toFixed(0)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {q.search_volume?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(q.status)}>
                          {q.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {questions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No questions discovered yet. Click "Discover Questions" to start.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Generation Tab */}
        <TabsContent value="generation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>FAQ Generation</CardTitle>
              <CardDescription>
                Generate FAQ answers for classified questions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Select value={selectedProduct} onValueChange={(val) => {
                  setSelectedProduct(val);
                  // Fetch FAQs when product changes
                  if (val) {
                    authFetch(`/api/demand/faqs/${val}`)
                      .then(r => r.json())
                      .then(data => setFaqs(data.faqs || []))
                      .catch(console.error);
                  }
                }}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleGenerateFAQs}
                  disabled={faqLoading || !selectedProduct}
                  className="flex items-center gap-2"
                >
                  {faqLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Generate FAQs
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* FAQs Table */}
          {selectedProduct && (
            <Card>
              <CardHeader>
                <CardTitle>Generated FAQs</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Grounding</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {faqs.map((faq) => (
                      <TableRow key={faq.id}>
                        <TableCell className="max-w-md">
                          <div>
                            <div className="font-medium line-clamp-1">{faq.question_text}</div>
                            <div className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {faq.answer_text}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress
                              value={parseFloat(String(faq.grounding_score)) * 100}
                              className="w-16"
                            />
                            <span className="text-sm">
                              {(parseFloat(String(faq.grounding_score)) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getSourceBadgeColor(faq.source_type)}>
                            {faq.source_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeColor(faq.status)}>
                            {faq.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {faq.status === 'draft' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                                onClick={() => handleApproveFAQ(faq.id)}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleRejectFAQ(faq.id, 'Low quality')}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {faqs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No FAQs generated yet. Select a product and click "Generate FAQs".
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Roadmap Tab */}
        <TabsContent value="roadmap" className="space-y-4">
          {/* Roadmap Stats */}
          {roadmapStats && (
            <div className="grid grid-cols-5 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{roadmapStats.total || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Pending
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">
                    {roadmapStats.pending || 0}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Assigned
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {roadmapStats.assigned || 0}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    In Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    {roadmapStats.in_progress || 0}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {roadmapStats.completed || 0}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Knowledge Creation Roadmap</CardTitle>
                  <CardDescription>
                    Questions requiring expert input or research
                  </CardDescription>
                </div>
                <Button
                  onClick={handleCreateRoadmap}
                  disabled={roadmapLoading}
                  className="flex items-center gap-2"
                >
                  {roadmapLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lightbulb className="h-4 w-4" />
                  )}
                  Create Roadmap Items
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Question</TableHead>
                    <TableHead>Authority</TableHead>
                    <TableHead>Methodology</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Demand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roadmapItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="max-w-md">
                        <span className="line-clamp-2">{item.question_text}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getAuthorityBadgeColor(item.authority_required)}>
                          {item.authority_required?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {item.research_methodology?.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getPriorityBadgeColor(item.priority)}>
                          {item.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={parseFloat(String(item.demand_score))} className="w-16" />
                          <span className="text-sm">{parseFloat(String(item.demand_score))?.toFixed(0)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(item.status)}>
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.assigned_expert_name || (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {roadmapItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No roadmap items yet. Classify questions first, then create roadmap items.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Validation Tab */}
        <TabsContent value="validation" className="space-y-4">
          {dashboard && (
            <>
              {/* Dashboard Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total FAQs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.stats.total_faqs}</div>
                    <div className="text-sm text-muted-foreground">
                      {dashboard.stats.new_questions} new questions
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Pending Review
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">
                      {parseInt(dashboard.stats.draft) + parseInt(dashboard.stats.pending_review)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Approved
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {dashboard.stats.approved}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Avg Grounding Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(parseFloat(dashboard.stats.avg_grounding_score) * 100).toFixed(0)}%
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quality Distribution & Sources */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Quality Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.quality_distribution.map((tier) => (
                        <div key={tier.quality_tier} className="flex items-center gap-4">
                          <div className="w-24 capitalize">{tier.quality_tier}</div>
                          <Progress
                            value={(parseInt(tier.count) / parseInt(dashboard.stats.total_faqs)) * 100}
                            className="flex-1"
                          />
                          <div className="w-12 text-right">{tier.count}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Sources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.sources.map((source) => (
                        <div key={source.source_type} className="flex items-center gap-4">
                          <Badge className={getSourceBadgeColor(source.source_type)}>
                            {source.source_type}
                          </Badge>
                          <div className="flex-1">
                            {source.count} FAQs
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {(parseFloat(source.avg_grounding) * 100).toFixed(0)}% grounding
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Gaps */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Content Gaps</CardTitle>
                  <CardDescription>
                    High-demand questions not covered by existing FAQs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Question</TableHead>
                        <TableHead>Opportunity</TableHead>
                        <TableHead>Search Volume</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="w-24">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboard.top_gaps.map((gap, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="max-w-md">
                            <span className="line-clamp-2">{gap.question_text}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={parseFloat(gap.opportunity_score)} className="w-16" />
                              <span className="text-sm">{parseFloat(gap.opportunity_score).toFixed(0)}</span>
                            </div>
                          </TableCell>
                          <TableCell>{gap.search_volume?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={getSourceBadgeColor(gap.source_type)}>
                              {gap.source_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={!!generatingGapContent}
                              onClick={() => handleCreateGapContent(gap.question_text)}
                              title="Generate AEO content for this gap"
                            >
                              {generatingGapContent === gap.question_text ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Zap className="h-3 w-3 mr-1" />
                              )}
                              Create
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}

          {validationLoading && (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject FAQ</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this FAQ answer.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectingFaqId(null);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectProductFAQ}
              disabled={!rejectReason.trim()}
            >
              Reject FAQ
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
